import { useState } from "react";
import authFetch from "../authFetch";
import { useValidationToast, validateFields } from "../utils/validation";
import { ALL_MODULE_KEYS } from "../modules/shell/WorkspaceApp";
import { getEffectiveTaxonomy } from "../permissions/checks";

/* ── Super Admin: Task Engine Configs Panel ─────────────── */
export default function SATaskConfigsPanel({platform,dispatch}){
  const vt=useValidationToast();
  const configs=(platform.taskEngineConfigs||[]);
  /* Phase 6.2: the legacy platform.taskTypes blob is gone — the Task
     Type picker now reads from the taxonomy (seed + platform layer).
     getEffectiveTaxonomy treats state.taskTaxonomy as empty when the
     state arg is {}, so this SA-only call only sees the platform +
     seed layers, which is correct for SA config targeting. */
  const taxonomyFor=(m)=>getEffectiveTaxonomy({},{taskTaxonomy:platform.taskTaxonomy||[]},m);
  const [showEdit,setShowEdit]=useState(null);
  const [form,setForm]=useState(null);
  const [saving,setSaving]=useState(false);
  /* Q11 flush — one-shot migration that fans out the platform blob
     configs into a target workspace's task_engine_config rows. */
  const [flushWs,setFlushWs]=useState("");
  const [flushing,setFlushing]=useState(false);
  const flushToWorkspace=async()=>{
    if(!flushWs){vt.show("Pick a workspace first");return;}
    if(!window.confirm(`Copy ${configs.length} platform defaults into workspace "${flushWs}"? Existing rows with the same id will be overwritten.`))return;
    setFlushing(true);
    try{
      const r=await authFetch("/api/tasks",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"flush_platform_configs_to_workspace",ws_key:flushWs,target_ws_key:flushWs})});
      const d=await r.json().catch(()=>({}));
      if(r.ok)vt.show(`Flushed ${d.flushed||0} rows into ${flushWs}.`);
      else vt.show(`Flush failed: ${d.error||r.status}`);
    }catch(e){vt.show(`Flush failed: ${e.message}`);}
    finally{setFlushing(false);}
  };

  const openNew=()=>{
    setForm({id:"",sourceModule:"",taskType:"",label:"",labelAr:"",requiresApproval:false,approvalRoles:["owner"],requiresExpense:false,defaultPriority:"medium",defaultAssigneeRole:"",slaStartDays:"",slaDays:"",slaWarningPercent:"80",enabled:true});
    setShowEdit(true);
  };
  const openEdit=(c)=>{
    const sla=c.slaConfig||{};
    setForm({id:c.id,sourceModule:c.sourceModule,taskType:c.taskType,label:c.label||"",labelAr:c.labelAr||"",
      requiresApproval:!!c.requiresApproval,approvalRoles:c.approvalRoles||["owner"],
      requiresExpense:!!c.requiresExpense,defaultPriority:c.defaultPriority||"medium",
      defaultAssigneeRole:c.defaultAssigneeRole||"",
      slaStartDays:sla.expected_start_days||"",
      slaDays:sla.expected_completion_days||"",slaWarningPercent:sla.warning_threshold_percent||"80",
      enabled:c.enabled!==false});
    setShowEdit(true);
  };
  const saveConfig=async()=>{
    const m=validateFields([{ok:form.sourceModule,label:"Source Module"},{ok:form.taskType,label:"Task Type"}]);
    if(m.length){vt.show(m.join(", "));return;}
    const slaConfig={};
    if(form.slaStartDays)slaConfig.expected_start_days=parseFloat(form.slaStartDays);
    if(form.slaDays)slaConfig.expected_completion_days=parseFloat(form.slaDays);
    if(form.slaWarningPercent)slaConfig.warning_threshold_percent=parseInt(form.slaWarningPercent);
    const config={id:form.id||`tec_${Date.now()}`,sourceModule:form.sourceModule,taskType:form.taskType,label:form.label,labelAr:form.labelAr,
      requiresApproval:form.requiresApproval,approvalRoles:form.approvalRoles,requiresExpense:form.requiresExpense,
      defaultPriority:form.defaultPriority,defaultAssigneeRole:form.defaultAssigneeRole||null,slaConfig,enabled:form.enabled};
    /* Optimistic in-memory update — platform state auto-persists to cmd_platform
       via usePersistedReducer. We additionally POST to save_config so the row
       lands in task_engine_config (workspace-scoped table) which is what the
       realtime sync consumes. This is the Q7 persistence fix from
       refactor-plan-task-taxonomy.md (Phase 1 / Batch 1.3). */
    if(form.id){dispatch({type:"UPDATE_TASK_ENGINE_CONFIG",id:form.id,patch:config});}
    else{dispatch({type:"ADD_TASK_ENGINE_CONFIG",config});}
    setSaving(true);
    try{
      const r=await authFetch("/api/tasks",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"save_config",config})});
      if(!r.ok){const d=await r.json().catch(()=>({}));vt.show(`Save failed: ${d.error||r.status}`);}
    }catch(e){vt.show(`Save failed: ${e.message}`);}
    finally{setSaving(false);}
    setShowEdit(false);setForm(null);
  };
  const deleteConfig=(c)=>{if(!window.confirm(`Delete config "${c.label||c.taskType}"?`))return;dispatch({type:"REMOVE_TASK_ENGINE_CONFIG",id:c.id});};

  const approvalRoleOptions=["owner","manager","admin","supervisor"];

  return(
    <div style={{maxWidth:860}}>
      {vt.Toast}
      <div className="p-section-hd" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>Task Engine Configs</span>
        <button className="p-btn-p" style={{fontSize:12,padding:"6px 14px"}} onClick={openNew}>+ Add Config</button>
      </div>
      {/* Q11 flush: pick a workspace, copy platform defaults as editable rows. */}
      {(platform.workspaces||[]).length>0&&(
        <div className="p-card" style={{marginBottom:12,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{fontSize:12,color:"var(--cg500)",flex:"1 1 320px"}}>Fan out the {configs.length} platform default{configs.length===1?"":"s"} as editable rows in a workspace. Workspace can then override or delete them.</div>
          <select className="p-input" style={{fontSize:12,minWidth:200}} value={flushWs} onChange={e=>setFlushWs(e.target.value)}>
            <option value="">— target workspace —</option>
            {(platform.workspaces||[]).map(w=><option key={w.wsKey} value={w.wsKey}>{w.name||w.wsKey}</option>)}
          </select>
          <button className="p-btn-s" style={{fontSize:12,padding:"6px 14px"}} onClick={flushToWorkspace} disabled={!flushWs||flushing||!configs.length}>{flushing?"Flushing…":"Flush to workspace"}</button>
        </div>
      )}
      <div className="p-card">
        <table style={{width:"100%",fontSize:13,borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:"1px solid var(--cg200)",textAlign:"left"}}>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Module</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Task Type</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Label</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Approval</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Expense</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>SLA (days)</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Priority</th>
            <th style={{padding:"8px 10px",color:"var(--cg400)",fontWeight:600,fontSize:11,textTransform:"uppercase"}}>Status</th>
            <th style={{padding:"8px 10px"}}></th>
          </tr></thead>
          <tbody>
            {configs.map(c=><tr key={c.id} style={{borderBottom:"1px solid var(--cg100)"}}>
              <td style={{padding:"8px 10px",color:"var(--cink)",textTransform:"capitalize"}}>{(platform.platformModuleNames||{})[c.sourceModule]||c.sourceModule}</td>
              <td style={{padding:"8px 10px",color:"var(--cg500)"}}>{c.taskType}</td>
              <td style={{padding:"8px 10px",color:"var(--cink)"}}>{c.label||"—"}</td>
              <td style={{padding:"8px 10px"}}>{c.requiresApproval?<span style={{color:"var(--cgold)",fontWeight:600,fontSize:11}}>YES</span>:<span style={{color:"var(--cg400)"}}>No</span>}</td>
              <td style={{padding:"8px 10px"}}>{c.requiresExpense?<span style={{color:"var(--cgold)",fontWeight:600,fontSize:11}}>YES</span>:<span style={{color:"var(--cg400)"}}>No</span>}</td>
              <td style={{padding:"8px 10px",color:"var(--cg500)",fontFamily:"var(--fm)"}}>{c.slaConfig?.expected_completion_days||"—"}</td>
              <td style={{padding:"8px 10px",textTransform:"capitalize",color:"var(--cg500)"}}>{c.defaultPriority}</td>
              <td style={{padding:"8px 10px"}}>{c.enabled?<span style={{color:"#22c55e",fontSize:11,fontWeight:600}}>Active</span>:<span style={{color:"var(--cg400)",fontSize:11}}>Off</span>}</td>
              <td style={{padding:"8px 10px",display:"flex",gap:6}}>
                <button className="p-btn-s" style={{fontSize:10,padding:"3px 8px"}} onClick={()=>openEdit(c)}>Edit</button>
                <button className="p-btn-s" style={{fontSize:10,padding:"3px 8px",borderColor:"#ef4444",color:"#ef4444"}} onClick={()=>deleteConfig(c)}>Del</button>
              </td>
            </tr>)}
          </tbody>
        </table>
        {!configs.length&&<div style={{padding:24,textAlign:"center",color:"var(--cg400)",fontSize:13}}>No configs yet. Click "+ Add Config" to define task rules per module.</div>}
      </div>

      {showEdit&&form&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{setShowEdit(false);setForm(null);}}>
          <div style={{background:"var(--cw,#fff)",borderRadius:16,width:"100%",maxWidth:540,maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 48px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--cg100)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:16,fontWeight:600,color:"var(--cink)"}}>{form.id?"Edit Config":"New Task Config"}</div>
              <button className="p-btn-s" style={{fontSize:11,padding:"3px 8px"}} onClick={()=>{setShowEdit(false);setForm(null);}}>✕</button>
            </div>
            <div style={{padding:20,flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"flex",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Module</div>
                  <select className="p-input" style={{width:"100%"}} value={form.sourceModule} onChange={e=>setForm(p=>({...p,sourceModule:e.target.value,taskType:""}))}>
                    <option value="">— select module —</option>
                    {ALL_MODULE_KEYS.map(mk=><option key={mk} value={mk}>{(platform.platformModuleNames||{})[mk]||mk}</option>)}
                  </select>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Task Type</div>
                  <select className="p-input" style={{width:"100%"}} value={form.taskType} onChange={e=>setForm(p=>({...p,taskType:e.target.value}))}>
                    <option value="">— select type —</option>
                    {(form.sourceModule?taxonomyFor(form.sourceModule):[]).map(tt=><option key={tt.id} value={tt.value}>{tt.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{display:"flex",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Label</div>
                  <input className="p-input" style={{width:"100%"}} value={form.label} onChange={e=>setForm(p=>({...p,label:e.target.value}))} placeholder="Config label"/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Label (Arabic)</div>
                  <input className="p-input" style={{width:"100%"}} dir="rtl" value={form.labelAr} onChange={e=>setForm(p=>({...p,labelAr:e.target.value}))}/>
                </div>
              </div>
              <div style={{display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
                <label style={{display:"flex",gap:6,alignItems:"center",fontSize:13,color:"var(--cink)",cursor:"pointer"}}><input type="checkbox" checked={form.requiresApproval} onChange={e=>setForm(p=>({...p,requiresApproval:e.target.checked}))}/> Requires Approval</label>
                <label style={{display:"flex",gap:6,alignItems:"center",fontSize:13,color:"var(--cink)",cursor:"pointer"}}><input type="checkbox" checked={form.requiresExpense} onChange={e=>setForm(p=>({...p,requiresExpense:e.target.checked}))}/> Requires Expense</label>
                <label style={{display:"flex",gap:6,alignItems:"center",fontSize:13,color:"var(--cink)",cursor:"pointer"}}><input type="checkbox" checked={form.enabled} onChange={e=>setForm(p=>({...p,enabled:e.target.checked}))}/> Enabled</label>
              </div>
              {form.requiresApproval&&<div>
                <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Approval Roles</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {approvalRoleOptions.map(r=><label key={r} style={{display:"flex",gap:4,alignItems:"center",fontSize:12,color:"var(--cg500)",cursor:"pointer"}}>
                    <input type="checkbox" checked={(form.approvalRoles||[]).includes(r)} onChange={e=>{const arr=form.approvalRoles||[];setForm(p=>({...p,approvalRoles:e.target.checked?[...arr,r]:arr.filter(x=>x!==r)}));}}/>
                    {r}
                  </label>)}
                </div>
              </div>}
              <div style={{display:"flex",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Default Priority</div>
                  <select className="p-input" style={{width:"100%"}} value={form.defaultPriority} onChange={e=>setForm(p=>({...p,defaultPriority:e.target.value}))}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Default Assignee Role</div>
                  <select className="p-input" style={{width:"100%"}} value={form.defaultAssigneeRole} onChange={e=>setForm(p=>({...p,defaultAssigneeRole:e.target.value}))}>
                    <option value="">— none —</option>
                    {["driver","maid","staff","manager","supervisor"].map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:"var(--cg500)",textTransform:"uppercase",letterSpacing:".07em",marginTop:4}}>SLA Settings</div>
              <div style={{display:"flex",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Expected Start (Days)</div>
                  <input className="p-input" style={{width:"100%"}} type="number" step="0.5" min="0" value={form.slaStartDays} onChange={e=>setForm(p=>({...p,slaStartDays:e.target.value}))} placeholder="e.g. 0.5"/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Expected Completion (Days)</div>
                  <input className="p-input" style={{width:"100%"}} type="number" step="0.5" min="0" value={form.slaDays} onChange={e=>setForm(p=>({...p,slaDays:e.target.value}))} placeholder="e.g. 2"/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--cg400)",marginBottom:4,textTransform:"uppercase"}}>Warning Threshold (%)</div>
                  <input className="p-input" style={{width:"100%"}} type="number" min="0" max="100" value={form.slaWarningPercent} onChange={e=>setForm(p=>({...p,slaWarningPercent:e.target.value}))} placeholder="80"/>
                </div>
              </div>
            </div>
            <div style={{padding:"14px 20px",borderTop:"1px solid var(--cg100)",display:"flex",justifyContent:"flex-end",gap:8}}>
              <button className="p-btn-s" onClick={()=>{setShowEdit(false);setForm(null);}}>Cancel</button>
              <button className="p-btn-p" onClick={saveConfig} disabled={saving}>{saving?"Saving…":"Save Config"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
